import { useState } from 'react';
import { FiX } from 'react-icons/fi';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
  loading?: boolean;
};

export function EmailGateModal({ open, onClose, onSubmit, loading }: Props) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    try {
      await onSubmit(email.trim());
    } catch {
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <div
      className='fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4'
      role='dialog'
      aria-modal='true'
      aria-labelledby='email-gate-title'
    >
      <div className='relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl'>
        <button
          type='button'
          className='absolute right-4 top-4 rounded-full p-1 text-gray-500 hover:bg-gray-100'
          onClick={onClose}
          aria-label='Close'
        >
          <FiX className='h-5 w-5' />
        </button>
        <h2 id='email-gate-title' className='text-xl font-bold text-gray-900 pr-8'>
          Email your PDF
        </h2>
        <p className='mt-2 text-sm text-gray-600'>
          We&apos;ll send a copy to your inbox and attach your parenting plan as a PDF. You can
          continue editing this draft anytime with your link.
        </p>
        <form onSubmit={handleSubmit} className='mt-6 space-y-4'>
          <div>
            <label htmlFor='pp-email' className='mb-1 block text-sm font-medium text-gray-700'>
              Email
            </label>
            <Input
              id='pp-email'
              type='email'
              autoComplete='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='you@example.com'
              required
            />
          </div>
          {error ? <p className='text-sm text-red-600'>{error}</p> : null}
          <div className='flex gap-3'>
            <Button type='button' variant='secondary' className='flex-1' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' className='flex-1' loading={loading} disabled={loading}>
              Download PDF
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
